import React, { useState, useEffect, useRef } from 'react'
import SettingsDropdown from './SettingsDropdown.jsx'
import { useAuth } from '../context/AuthContext.jsx'

const NAV_SECTIONS = [
  {
    id: 'dashboard',
    label: 'Dashboard',
    direct: true,
    pageId: 'dashboard',
  },
  {
    id: 'wellbeing',
    label: 'Wellbeing',
    pages: [
      { id: 'wellbeing',             label: 'Overview' },
      { id: 'wellbeing/mood',        label: 'Mood' },
      { id: 'wellbeing/activity',    label: 'Activity' },
      { id: 'wellbeing/trends',      label: 'Trends' },
      { id: 'wellbeing/reflections', label: 'Reflections' },
    ],
  },
  {
    id: 'hobbies',
    label: 'Hobbies',
    pages: [
      { id: 'hobbies/gardening',  label: 'Gardening' },
      { id: 'hobbies/cooking',    label: 'Cooking' },
      { id: 'hobbies/writing',    label: 'Writing' },
      { id: 'hobbies/reading',    label: 'Reading' },
      { id: 'hobbies/journaling', label: 'Journaling' },
    ],
  },
  {
    id: 'challenges',
    label: 'Challenges',
    pages: [
      { id: 'challenges',           label: 'Today' },
      { id: 'challenges/active',    label: 'Active Challenges' },
      { id: 'challenges/explore',   label: 'Explore' },
      { id: 'challenges/vault',     label: 'Adventure Vault' },
      { id: 'challenges/completed', label: 'Completed' },
    ],
  },
  {
    id: 'my-space',
    label: 'My Space',
    pages: [
      { id: 'my-space',          label: 'Overview' },
      { id: 'my-space/pages',    label: 'My Pages' },
      { id: 'my-space/trackers', label: 'My Trackers' },
      { id: 'my-space/goals',    label: 'My Goals' },
    ],
  },
]

function NavSection({ section, activePage, onNavigate, isOpen, onToggle }) {
  const isActive = section.direct
    ? activePage === section.pageId
    : section.pages.some(p => p.id === activePage)

  if (section.direct) {
    return (
      <div className="nav-section">
        <span
          className={`nav-section-label ${isActive ? 'active' : ''}`}
          role="button"
          tabIndex={0}
          onClick={() => onNavigate(section.pageId)}
          onKeyDown={e => e.key === 'Enter' && onNavigate(section.pageId)}
        >
          {section.label}
        </span>
      </div>
    )
  }

  function handleItemClick(pageId) {
    onNavigate(pageId)
    onToggle(null)
  }

  return (
    <div className="nav-section">
      <span
        className={`nav-section-label ${isActive ? 'active' : ''} ${isOpen ? 'open' : ''}`}
        role="button"
        tabIndex={0}
        onClick={onToggle}
        onKeyDown={e => e.key === 'Enter' && onToggle()}
      >
        {section.label}
        <span className="nav-section-arrow">{isOpen ? '▲' : '▼'}</span>
      </span>
      {isOpen && (
        <div className="nav-dropdown">
          {section.pages.map(page => (
            <button
              key={page.id}
              className={`nav-dropdown-item ${activePage === page.id ? 'active' : ''}`}
              onClick={() => handleItemClick(page.id)}
            >
              {page.label}
            </button>
          ))}
        </div>
      )}
    </div>
  )
}

function UserMenu({ user, onAccount, onLogout }) {
  const [open, setOpen] = useState(false)
  const menuRef = useRef(null)

  useEffect(() => {
    function handleClickOutside(e) {
      if (menuRef.current && !menuRef.current.contains(e.target)) setOpen(false)
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const initial = (user.displayName || user.email || '?')[0].toUpperCase()

  return (
    <div ref={menuRef} style={{ position: 'relative' }}>
      <button
        onClick={() => setOpen(o => !o)}
        title={user.displayName || user.email}
        style={{
          width: 32, height: 32, borderRadius: '50%',
          background: user.avatarUrl ? 'transparent' : 'var(--accent)',
          color: 'white',
          border: 'none', cursor: 'pointer',
          fontSize: '0.82rem', fontWeight: 600, fontFamily: 'inherit',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          flexShrink: 0, overflow: 'hidden', padding: 0,
        }}
      >
        {user.avatarUrl ? (
          <img
            src={user.avatarUrl}
            alt={user.displayName || user.email}
            referrerPolicy="no-referrer"
            style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
          />
        ) : initial}
      </button>

      {open && (
        <div style={{
          position: 'absolute', right: 0, top: 'calc(100% + 8px)',
          background: 'var(--surf)', borderRadius: '12px',
          boxShadow: '0 4px 20px rgba(0,0,0,0.13)',
          border: '1px solid rgba(0,0,0,0.08)',
          minWidth: '200px', zIndex: 200, overflow: 'hidden',
        }}>
          <div style={{ padding: '0.85rem 1rem', borderBottom: '1px solid rgba(0,0,0,0.06)' }}>
            <p style={{ fontWeight: 600, fontSize: '0.88rem', color: 'var(--ink)', margin: '0 0 2px' }}>
              {user.displayName}
            </p>
            <p style={{ fontSize: '0.75rem', color: 'var(--stone)', margin: 0 }}>{user.email}</p>
            <p style={{ fontSize: '0.68rem', color: 'var(--accent)', textTransform: 'uppercase', letterSpacing: '0.08em', margin: '3px 0 0' }}>
              {user.role}
            </p>
          </div>
          <button
            onClick={() => { setOpen(false); onAccount() }}
            style={{
              display: 'block', width: '100%', padding: '0.7rem 1rem',
              background: 'none', border: 'none', textAlign: 'left',
              cursor: 'pointer', fontFamily: 'inherit', fontSize: '0.83rem',
              color: 'var(--ink)', borderBottom: '1px solid rgba(0,0,0,0.06)',
            }}
          >
            Account settings
          </button>
          <button
            onClick={() => { setOpen(false); onLogout() }}
            style={{
              display: 'block', width: '100%', padding: '0.7rem 1rem',
              background: 'none', border: 'none', textAlign: 'left',
              cursor: 'pointer', fontFamily: 'inherit', fontSize: '0.83rem',
              color: '#c0392b',
            }}
          >
            Sign out
          </button>
        </div>
      )}
    </div>
  )
}

export default function Nav({ activePage, onNavigate, onCapture }) {
  const { user, logout } = useAuth()
  const [scrolled,       setScrolled]       = useState(false)
  const [openSection,    setOpenSection]    = useState(null)
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
  const navRef = useRef(null)

  useEffect(() => {
    function handleScroll() { setScrolled(window.scrollY > 20) }
    window.addEventListener('scroll', handleScroll, { passive: true })
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  useEffect(() => {
    function handleClickOutside(e) {
      if (navRef.current && !navRef.current.contains(e.target)) setOpenSection(null)
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  // Close mobile menu on Escape key
  useEffect(() => {
    if (!mobileMenuOpen) return
    function onKey(e) { if (e.key === 'Escape') setMobileMenuOpen(false) }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [mobileMenuOpen])

  // Close mobile menu whenever the active page changes
  useEffect(() => { setMobileMenuOpen(false) }, [activePage])

  function toggleSection(sectionId) {
    setOpenSection(prev => prev === sectionId ? null : sectionId)
  }

  async function handleLogout() {
    await logout()
  }

  function handleMobileNav(pageId) {
    onNavigate(pageId)
    setMobileMenuOpen(false)
    setOpenSection(null)
  }

  return (
    <>
      <nav className={scrolled ? 'scrolled' : ''} ref={navRef}>
        <a
          href="#"
          className="nav-logo"
          onClick={e => { e.preventDefault(); onNavigate('dashboard'); setOpenSection(null) }}
        >
          Wellness for Soul
        </a>

        <div className="nav-right">
          <div className="nav-sections">
            {NAV_SECTIONS.map(section => (
              <NavSection
                key={section.id}
                section={section}
                activePage={activePage}
                onNavigate={onNavigate}
                isOpen={openSection === section.id}
                onToggle={() => toggleSection(section.id)}
              />
            ))}
          </div>
          <div style={{ position: 'relative' }}>
            <SettingsDropdown />
          </div>
          {user && (
            <UserMenu
              user={user}
              onAccount={() => { onNavigate('account-settings'); setOpenSection(null) }}
              onLogout={handleLogout}
            />
          )}
          <button
            className="nav-hamburger"
            onClick={() => setMobileMenuOpen(o => !o)}
            aria-label="Open menu"
            aria-expanded={mobileMenuOpen}
          >
            <span className="hamburger-line" />
            <span className="hamburger-line" />
            <span className="hamburger-line" />
          </button>
        </div>
      </nav>

      {mobileMenuOpen && (
        <div className="nav-mobile-overlay" onClick={() => setMobileMenuOpen(false)}>
          <div className="nav-mobile-menu" onClick={e => e.stopPropagation()}>
            <button
              className="nav-mobile-close"
              onClick={() => setMobileMenuOpen(false)}
              aria-label="Close menu"
            >
              ✕
            </button>

            <div className="nav-mobile-sections">
              <button
                className={`nav-mobile-direct${activePage === 'dashboard' ? ' active' : ''}`}
                onClick={() => handleMobileNav('dashboard')}
              >
                Dashboard
              </button>

              {NAV_SECTIONS.filter(s => !s.direct).map(section => (
                <div key={section.id}>
                  <span className="nav-mobile-section-title">{section.label}</span>
                  <div className="nav-mobile-subitems">
                    {section.pages.map(page => (
                      <button
                        key={page.id}
                        className={activePage === page.id ? 'active' : ''}
                        onClick={() => handleMobileNav(page.id)}
                      >
                        {page.label}
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </div>

            <div className="nav-mobile-footer">
              <div style={{ position: 'relative' }}>
                <SettingsDropdown />
              </div>
              {user && (
                <>
                  <button
                    className="nav-mobile-footer-btn"
                    onClick={() => handleMobileNav('account-settings')}
                  >
                    Account
                  </button>
                  <button
                    className="nav-mobile-signout-btn"
                    onClick={() => { setMobileMenuOpen(false); handleLogout() }}
                  >
                    Sign out
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  )
}
